import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { fieldSx, optionListSx, optionRowSx } from "./filterPanelStyles";
import { MOBILE_TOUCH_MIN_PX } from "../../../theme/mobileLayout";
import { rankOptionMatches } from "./optionMatching";

/**
 * Mobbin-inspired searchable checklist / single-select list used inside filter menus.
 * Keeps current selections visible and filterable.
 */
export default function SearchableOptionList({
  options = [],
  value,
  onChange,
  multiple = false,
  placeholder = "Search...",
  getOptionLabel = (option) => String(option ?? ""),
  getOptionKey = (option) => getOptionLabel(option),
  emptyLabel = "No matches",
  autoFocus = true,
  showSelectionControl,
  /** When true, onChange receives the option object/value itself (not just its label). */
  selectOption = false,
  /** Options always shown above the main list when they match the query (e.g. "All companies"). */
  leadingOptions = [],
  /** Optional trailing control per row (e.g. delete). Receives (option, { selected }). */
  renderOptionEnd = null,
  /** Hide the built-in search field (use an external input, e.g. V4 SelectedChipsArea). */
  hideSearch = false,
  /** Controlled search query when hideSearch is true (or when syncing an external input). */
  query: queryProp,
  onQueryChange,
  mobileTouchTargets = false,
  /** Cap on rendered unselected rows; large catalogs (3k+ locations) freeze the page otherwise. */
  maxVisibleOptions = Infinity,
}) {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [internalQuery, setInternalQuery] = useState("");
  const isQueryControlled = queryProp !== undefined;
  const query = isQueryControlled ? String(queryProp ?? "") : internalQuery;
  const setQuery = (next) => {
    if (!isQueryControlled) setInternalQuery(next);
    onQueryChange?.(next);
  };
  const [hoveredKey, setHoveredKey] = useState(null);
  const showControl = showSelectionControl ?? multiple;

  useEffect(() => {
    if (!autoFocus || hideSearch) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(timer);
  }, [autoFocus, hideSearch]);

  const selectedKeys = useMemo(() => {
    if (multiple) {
      return new Set((value || []).map((item) => getOptionKey(item)));
    }
    return new Set(value != null && value !== "" ? [getOptionKey(value)] : []);
  }, [getOptionKey, multiple, value]);

  const optionsWithSelection = useMemo(() => {
    const merged = [...options];
    const seen = new Set(merged.map((option) => getOptionKey(option)));
    const selectedValues = multiple
      ? (value || [])
      : value != null && value !== ""
        ? [value]
        : [];

    selectedValues.forEach((selected) => {
      const key = getOptionKey(selected);
      if (!seen.has(key) && !leadingOptions.some((option) => getOptionKey(option) === key)) {
        seen.add(key);
        merged.unshift(selected);
      }
    });
    return merged;
  }, [getOptionKey, leadingOptions, multiple, options, value]);

  const filteredLeading = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const leading = leadingOptions || [];
    if (!normalized) return leading;
    return leading.filter((option) =>
      getOptionLabel(option).toLowerCase().includes(normalized)
    );
  }, [getOptionLabel, leadingOptions, query]);

  const filtered = useMemo(
    () => rankOptionMatches(optionsWithSelection, query, getOptionLabel),
    [getOptionLabel, optionsWithSelection, query]
  );

  const ordered = useMemo(() => {
    const selected = [];
    const rest = [];
    filtered.forEach((option) => {
      if (selectedKeys.has(getOptionKey(option))) selected.push(option);
      else rest.push(option);
    });
    const restLimit = Math.max(0, maxVisibleOptions - selected.length);
    return { selected, rest: rest.slice(0, restLimit), hiddenCount: Math.max(0, rest.length - restLimit) };
  }, [filtered, getOptionKey, maxVisibleOptions, selectedKeys]);

  const resolveNextValue = (option) => {
    if (selectOption) return option;
    return typeof option === "string" ? option : getOptionLabel(option);
  };

  const toggleOption = (option) => {
    const key = getOptionKey(option);
    if (multiple) {
      const current = Array.isArray(value) ? [...value] : [];
      const index = current.findIndex((item) => getOptionKey(item) === key);
      if (index >= 0) current.splice(index, 1);
      else current.push(resolveNextValue(option));
      onChange(current);
      return;
    }

    if (value != null && getOptionKey(value) === key) onChange(null);
    else onChange(resolveNextValue(option));
  };

  const renderRow = (option) => {
    const key = getOptionKey(option);
    const label = getOptionLabel(option);
    const selected = selectedKeys.has(key);
    const endAdornment = typeof renderOptionEnd === "function" ? renderOptionEnd(option, { selected }) : null;
    return (
      <Box
        key={key}
        component="button"
        type="button"
        role={multiple ? "checkbox" : "option"}
        aria-checked={selected}
        aria-selected={selected}
        onMouseEnter={() => setHoveredKey(key)}
        onMouseLeave={() => setHoveredKey(null)}
        onClick={() => toggleOption(option)}
        sx={{
          ...optionRowSx(theme, {
            selected,
            highlighted: hoveredKey === key,
          }),
          ...(mobileTouchTargets ? { minHeight: MOBILE_TOUCH_MIN_PX } : null),
          justifyContent: endAdornment ? "space-between" : "flex-start",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: "1 1 auto" }}>
          {showControl ? (
            <Checkbox
              size="small"
              checked={selected}
              tabIndex={-1}
              disableRipple
              sx={{
                p: "0 !important",
                m: "0 !important",
                flex: "0 0 auto",
                width: 18,
                height: 18,
                color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.35)" : "rgba(88,89,91,0.45)",
                "&.Mui-checked": {
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.primary.main,
                },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            />
          ) : null}
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: selected ? 650 : 500,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        </Box>
        {endAdornment}
      </Box>
    );
  };

  return (
    <Box>
      {!hideSearch ? (
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          inputRef={inputRef}
          sx={[fieldSx(theme), mobileTouchTargets && {
            "& .MuiInputBase-root": { minHeight: MOBILE_TOUCH_MIN_PX },
          }]}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      ) : null}

      <Box
        sx={{
          ...optionListSx(theme),
          ...(hideSearch ? { mt: 0 } : null),
        }}
        role={multiple ? "group" : "listbox"}
      >        {filteredLeading.length === 0 && filtered.length === 0 ? (
          <Typography
            sx={{
              px: 1,
              py: 1.25,
              fontSize: 12.5,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            {emptyLabel}
          </Typography>
        ) : (
          <>
            {filteredLeading.map(renderRow)}
            {filteredLeading.length > 0 && (ordered.selected.length > 0 || ordered.rest.length > 0) ? (
              <Divider
                sx={{
                  my: 0.5,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(88,89,91,0.12)",
                }}
              />
            ) : null}
            {ordered.selected.map(renderRow)}
            {ordered.selected.length > 0 && ordered.rest.length > 0 ? (
              <Divider
                sx={{
                  my: 0.5,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(88,89,91,0.12)",
                }}
              />
            ) : null}
            {ordered.rest.map(renderRow)}
            {ordered.hiddenCount > 0 ? (
              <Typography sx={{ px: 1, py: 0.75, fontSize: 12, color: "text.secondary" }}>
                {`Showing ${(ordered.selected.length + ordered.rest.length).toLocaleString("en-US")} of ${filtered.length.toLocaleString("en-US")}. Type to narrow.`}
              </Typography>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
}
